import { sanitize } from 'dompurify';
import { i18n, TFunction } from 'i18next';
import { Doc } from '~src/domains/common/enums/docs';
import { Language, Locale } from '~src/domains/common/enums/localization';
import {
  general,
  setDocument,
  setIsProcessing,
  setLazyDocs,
} from '~src/domains/common/state/commonReducer';
import { LazyDocs } from '~src/domains/common/types/common';
import { changeLocale } from '../helpers';
import { trackDocumentRequest } from '../tracking';
import { jsonpDoc } from './jsonp';

const scrollDoc = (id: string, language: Language) => {
  const docId = `${id.toLowerCase()}-${language}`;
  const element =
    document.querySelector(`[name="${docId}"]`) ||
    document.getElementById(docId);

  if (element && element instanceof HTMLElement && element.offsetParent) {
    // If the first doc, scroll all the way to top showing margins instead
    if (docId === 'til-en') {
      document.querySelector('.multi-document')?.scroll({ top: 0 });
    } else {
      element.scrollIntoView();
    }

    window.scrollTo(0, 0);
  }
};

const lazyLoadDocument =
  (url: string, id: Doc) => (dispatch: any, getState: Function) => {
    const state = getState();
    const currDoc = state.common.document;
    dispatch(trackDocumentRequest(id));
    dispatch(
      general({
        isProcessing: false,
        lazyDocsInFlight: true,
      }),
    );
    dispatch(
      setDocument({
        ...currDoc,
        id,
        url,
      }),
    );
    dispatch(jsonpDoc(url, id));
  };

export const translateBigDocument =
  (id: Doc, language: Language) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const currDoc = state.common.document;
    dispatch(
      setDocument({
        ...currDoc,
        id,
      }),
    );
    scrollDoc(id, language);
  };

export const updateDisclosureForms =
  (id: Doc, data: any) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const { session, locale } = state.common;
    const docOrder = state.loanConsent.docOrder;
    const autoPayEnabled = state.common.autoPayEnabled;

    const [language, _] = locale.split('-');
    const firstDocName = docOrder?.[0];
    const docIndex = docOrder?.indexOf(id);
    const nextDocId: Doc | null =
      docOrder && docOrder.includes(id) && docOrder[docIndex + 1]
        ? docOrder[docIndex + 1]
        : null;

    let nextDocUrl: string | undefined;
    const commonDocs = session?.common_docs;
    const sessionLoanOffers =
      session?.product_offers?.offers || session?.loan_offers || [];
    const loanOfferDocs = sessionLoanOffers[0]?.docs;

    if (nextDocId) {
      if (commonDocs) {
        nextDocUrl = commonDocs[nextDocId].url;
      } else if (loanOfferDocs) {
        nextDocUrl = loanOfferDocs[nextDocId].url;
      }
    }

    const html = data;
    const isLastDoc = !nextDocId || (!autoPayEnabled && nextDocId === Doc.DCD);

    dispatch(
      setLazyDocs({
        [id]: html,
      }),
    );

    if (isLastDoc) {
      dispatch(
        general({
          lazyDocsInFlight: false,
        }),
      );
      setTimeout(() => {
        dispatch(translateBigDocument(firstDocName || id, language));
      }, 500);
    } else {
      if (nextDocId && nextDocUrl) {
        dispatch(lazyLoadDocument(nextDocUrl, nextDocId));
      }
    }
  };

export const loadDocument =
  (url: string, id: Doc) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const currDoc = state.common.document;

    dispatch(trackDocumentRequest(id));

    dispatch(setIsProcessing(true));
    dispatch(
      setDocument({
        ...currDoc,
        url,
        id,
      }),
    );
    dispatch(jsonpDoc(url, id));
  };

export const formatDocumentLinks = () => {
  const anchorLinks = document.querySelectorAll('.anchor-link');

  Array.from(anchorLinks).forEach(el => {
    const anchorName = el.getAttribute('href')?.substr(1);
    el.setAttribute(
      'onClick',
      `event.preventDefault(); document.querySelector('a[name="${anchorName}"]').scrollIntoView()`,
    );
  });
};

export const translateDocument =
  (docId: string, locale: Locale, i18n: i18n) => (dispatch: Function) => {
    const [language, _] = locale.split('-');
    dispatch(changeLocale(locale, i18n));
    scrollDoc(docId, language as Language);
  };

export const generateDoc = (lazyDocs: LazyDocs, doc: Doc, t: TFunction) => {
  const docHtml = lazyDocs[doc];

  return (
    <div
      id={`${doc.toUpperCase()}`}
      dangerouslySetInnerHTML={{
        __html: docHtml ? sanitize(docHtml) : '',
      }}
    ></div>
  );
};

/**
 * Gets the list of external documents from commonDocs that should be displayed
 * as links that open in a new tab, not loaded inline via XHR
 */
export const getExternalDocumentOrder = (
  commonDocs: { [key in Doc]?: any } | undefined,
): Doc[] => {
  if (!commonDocs) {
    return [];
  }

  const externalDocs: Doc[] = [];
  const isWdEnabled = Doc.WD in commonDocs;

  if (isWdEnabled) {
    externalDocs.push(Doc.WD);
  }

  return externalDocs;
};
